import React, { memo, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import ReactDOM from 'react-dom';

import { Badge, DropdownMenu, DropdownToggle, Input, UncontrolledDropdown, UncontrolledTooltip } from 'reactstrap';
import { TableColumn } from 'react-data-table-component';
import { IDataTableColumn } from 'react-data-table-component-footer';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import arrayMove from 'array-move';

import { CompactUserResponse, UserId, UserResponse } from '@business/entities/account';
import { OrgId } from '@business/entities/organization';
import {
  CompactSupportTicketResponse,
  SupportTicketActionName,
  SupportTicketPriority,
  SupportTicketStatus,
  TicketAdditionalResponse,
  TicketId,
  TicketSearchParams,
} from '@business/entities/SupportTicket';
import { SupportTicketStatusResponse, SupportTicketStatusId } from '@business/entities/support_ticket_status';
import { CommonService, HTMLService } from '@business/services';
import { TicketService } from '@business/services/ticket_service';
import { Button } from '@core/components/Button';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { formControl, formGroup, useFormConfig } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { PaginatedController } from '@core/utils/paginated';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLazyEffect } from '@core/utils/react';
import { openSelectOrgModal } from '@modules/common';
import { openTicketOrg } from '@modules/support/TicketDetailPage/Components/TicketOrgModal';

import './TicketDragDrop.scss';

const PRESET_COLORS = [
  '#9e9e9e',
  '#4caf50',
  '#8bc34a',
  '#ffeb3b',
  '#2196f3',
  '#3f51b5',
  '#9c27b0',
  '#00bcd4',
  '#f44336',
  '#ff9800',
  '#e91e63',
  '#000000',
];

const STATUS_ICON_MAP: Record<string, { icon: string; color: string; label: string }> = {
  [SupportTicketStatus.open]: { icon: 'fa fa-circle', color: '#f1b44c', label: 'Open' },
  [SupportTicketStatus.replied]: { icon: 'fa fa-reply', color: '#556ee6', label: 'Replied' },
  [SupportTicketStatus.closed]: { icon: 'fa fa-check-circle', color: '#34c38f', label: 'Closed' },
  [SupportTicketStatus.markNotification]: { icon: 'fa fa-bell', color: '#343a40', label: 'Notification' },
  [SupportTicketStatus.markSpam]: { icon: 'fa fa-ban', color: '#f46a6a', label: 'Spam' },
};

interface CustomStatusDropdownProps {
  ticket: CompactSupportTicketResponse;
  statuses: SupportTicketStatusResponse[];
  onSelect(ticketId: TicketId, statusId: SupportTicketStatusId): void;
  onAddStatus(data: { name: string; color: string }): void;
}

const CustomStatusDropdown = memo((props: CustomStatusDropdownProps) => {
  const { ticket, statuses, onSelect, onAddStatus } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setNewName('');
      setNewColor(PRESET_COLORS[0]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleAdd = useCallback(() => {
    if (newName.trim()) {
      onAddStatus({ name: newName.trim(), color: newColor });
      setNewName('');
      setNewColor(PRESET_COLORS[0]);
      setShowAddForm(false);
    }
  }, [newName, newColor, onAddStatus]);

  const currentStatus = ticket.supportStatus;

  const dropdownContent = isOpen
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: 220,
            maxHeight: 350,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ overflowY: 'auto', maxHeight: 220, padding: '4px 0' }}>
            {statuses.map(status => (
              <div
                key={status.id}
                className="dropdown-item"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}
                onClick={() => {
                  onSelect(ticket.id, status.id);
                  setIsOpen(false);
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: status.color, flexShrink: 0 }} />
                <span>{status.name}</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #eee', padding: 8 }}>
            {!showAddForm ? (
              <a className="text-primary" style={{ cursor: 'pointer', fontSize: 13 }} onClick={() => setShowAddForm(true)}>
                + Add new status
              </a>
            ) : (
              <div>
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="Status name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  autoFocus
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                  {PRESET_COLORS.map(color => (
                    <span
                      key={color}
                      onClick={() => setNewColor(color)}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: color,
                        cursor: 'pointer',
                        border: newColor === color ? '2px solid #333' : '2px solid transparent',
                        display: 'inline-block',
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-primary" onClick={handleAdd}>
                    Add
                  </button>
                  <button className="btn btn-sm btn-link" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setIsOpen(!isOpen)}
        style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
      >
        {currentStatus ? (
          <>
            <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: currentStatus.color, display: 'inline-block' }} />
            {currentStatus.name}
          </>
        ) : (
          <span style={{ color: '#999' }}>Set Status</span>
        )}
        <i className="fa fa-caret-down" style={{ fontSize: 10, marginLeft: 2 }}></i>
      </button>
      {dropdownContent}
    </>
  );
});

interface StatusCellProps {
  ticket: CompactSupportTicketResponse;
  statuses: SupportTicketStatusResponse[];
  onSelectStatus(ticketId: TicketId, statusId: SupportTicketStatusId): void;
  onAddStatus(data: { name: string; color: string }): void;
}

const StatusCell = memo((props: StatusCellProps) => {
  const { ticket, statuses, onSelectStatus, onAddStatus } = props;
  const statusInfo = STATUS_ICON_MAP[ticket.status] || STATUS_ICON_MAP[SupportTicketStatus.open];

  return (
    <div className="d-flex align-items-center py-1" style={{ gap: 8 }}>
      <span id={`static-status-${ticket.id}`}>
        <i className={statusInfo.icon} style={{ color: statusInfo.color, fontSize: 14, cursor: 'default' }} />
      </span>
      <UncontrolledTooltip placement="top" target={`static-status-${ticket.id}`}>
        {statusInfo.label}
      </UncontrolledTooltip>
      <CustomStatusDropdown ticket={ticket} statuses={statuses} onSelect={onSelectStatus} onAddStatus={onAddStatus} />
    </div>
  );
});

interface SubjectItemProps {
  ticket: CompactSupportTicketResponse;
}

const SubjectItem = memo((props: SubjectItemProps) => {
  const { ticket } = props;

  return (
    <div className="py-2 d-flex" style={{ flex: 1, alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <Link to={`/support-tickets/detail/${ticket.id}`} className="text-primary">
          <h6 className="mb-1">{ticket.subject}</h6>
        </Link>
        <div className="font-size-12 pt-1 text-muted">{ticket.fromEmail}</div>
      </div>
      {ticket.tasks > 0 && (
        <span className="text-muted mr-3 font-size-12" style={{ opacity: 0.8 }}>
          <i className="fa fa-sitemap mr-2"></i>
          <span style={{ fontWeight: '600' }}>{`${ticket.completedTasks}/${ticket.tasks}`}</span>
        </span>
      )}

      <div className="d-flex flex-column align-items-end">
        <div id={`ticket-tooltip-${ticket.id}`} className="hover-pointer" style={{ padding: 5 }}>
          <i className="fa fa-eye"></i>
        </div>

        <UncontrolledTooltip placement="top" target={`ticket-tooltip-${ticket.id}`}>
          <div style={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            {HTMLService.toReact(ticket.lastThreadText)}
          </div>
        </UncontrolledTooltip>
        <div>
          {!R.isEmpty(ticket.categoryLabel) && (
            <Badge color={'info'} className={`badge-soft font-size-10 px-2 py-1 mr-2`}>
              {ticket.categoryLabel}
            </Badge>
          )}
          {ticket.importId === '0' && !R.isEmpty(ticket.tenantName) && (
            <Badge color={'primary'} className={`badge-soft font-size-10 px-2 py-1`}>
              Escalated to {ticket.tenantName}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
});

interface DraggableRowProps {
  ticket: CompactSupportTicketResponse;
  children: React.ReactNode;
}

const DraggableRow = memo(({ ticket, children }: DraggableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const rowClasses = ['ticket-draggable-row', isDragging ? 'is-dragging' : ''].filter(Boolean).join(' ');

  return (
    <div ref={setNodeRef} style={style} className={rowClasses}>
      <div className="ticket-drag-handle" {...attributes} {...listeners}>
        <div className="ticket-drag-handle-icon">
          <i className="bx bx-grid-vertical"></i>
        </div>
      </div>
      {children}
    </div>
  );
});

interface PaginationProps {
  currentPage: number;
  totalRows: number;
  rowsPerPage: number;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (perPage: number) => void;
}

const Pagination = memo((props: PaginationProps) => {
  const { currentPage, totalRows, rowsPerPage, onChangePage, onChangeRowsPerPage } = props;

  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startRow = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);

  return (
    <div className="ticket-draggable-pagination">
      <div className="ticket-pagination-left">
        <span style={{ marginRight: 8 }}>Show entries</span>
        <select className="ticket-pagination-select" value={rowsPerPage} onChange={e => onChangeRowsPerPage(Number(e.target.value))}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={40}>40</option>
          <option value={100}>100</option>
        </select>
      </div>

      <div className="ticket-pagination-center">
        <span>
          {startRow}-{endRow} of {totalRows}
        </span>
      </div>

      {/* Page navigation */}
      <div className="ticket-pagination-right">
        <button className="ticket-pagination-nav-button" disabled={currentPage === 1} onClick={() => onChangePage(1)} title="First page">
          <i className="bx bx-chevrons-left"></i>
        </button>

        <button
          className="ticket-pagination-nav-button"
          disabled={currentPage === 1}
          onClick={() => onChangePage(currentPage - 1)}
          title="Previous page"
        >
          <i className="bx bx-chevron-left"></i>
        </button>

        <button
          className="ticket-pagination-nav-button"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onChangePage(currentPage + 1)}
          title="Next page"
        >
          <i className="bx bx-chevron-right"></i>
        </button>

        <button
          className="ticket-pagination-nav-button"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onChangePage(totalPages)}
          title="Last page"
        >
          <i className="bx bx-chevrons-right"></i>
        </button>
      </div>
    </div>
  );
});

export interface TicketListingTableProps {
  paginated: PaginatedController<CompactSupportTicketResponse, string, TicketSearchParams, TicketAdditionalResponse>;
  users: CompactUserResponse[] | UserResponse[];
  tickets: CompactSupportTicketResponse[];
  supportTicketStatuses: SupportTicketStatusResponse[];
  onAssignTo(id: TicketId, action: SupportTicketActionName, actionId: string): Observable<any>;
  onUpdateBulk(ids: TicketId[], status: SupportTicketStatus): Observable<any>;
  updateTicketAction(ticketId: TicketId, action: SupportTicketActionName, actionId: string): Observable<any>;
  onAddSupportStatus(data: { name: string; color: string }): void;
  query: TicketSearchParams;
  onSaveOrder(channelId: string, orderedIds: TicketId[], page: number, perPage: number): Observable<any>;
  initialSearchString?: string;
  onColumnClick?: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

export const TicketListingTable = memo((props: TicketListingTableProps) => {
  const {
    tickets,
    users,
    paginated,
    onAssignTo,
    onUpdateBulk,
    updateTicketAction,
    supportTicketStatuses,
    onAddSupportStatus,
    initialSearchString,
    query,
    onSaveOrder,
    onColumnClick,
  } = props;
  const [selectedIds, setSelectedIds] = useState<TicketId[]>([]);
  const [toggledClearRows, setToggleClearSelectedRows] = useState(false);

  const [localTickets, setLocalTickets] = useState<CompactSupportTicketResponse[]>(tickets);
  const [activeId, setActiveId] = useState<TicketId | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [searchText, setSearchText] = useState(initialSearchString || '');

  const dragSaveSubscriptionRef = useRef<any>(null);
  const isManualSort = query.sortBy === 'manual';
  const pageMeta = useObservable(paginated.pageMeta$);

  // Update local tickets when props change
  useEffect(() => {
    setLocalTickets(tickets);
  }, [tickets]);

  useEffect(() => {
    return () => {
      if (dragSaveSubscriptionRef.current) {
        dragSaveSubscriptionRef.current.unsubscribe();
        dragSaveSubscriptionRef.current = null;
      }
    };
  }, []);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const form = useMemo(() => formGroup({ action: formControl<SupportTicketStatus>({}) }), []);

  const onSelectedRowsChange = useCallback(({ selectedRows }) => {
    const ids = (selectedRows as CompactSupportTicketResponse[]).map(t => t.id);
    setSelectedIds(ids);
    setToggleClearSelectedRows(false);
  }, []);

  const onPriorityUpdate = useCallback(
    (ticketId: TicketId, priority: SupportTicketPriority) => {
      updateTicketAction(ticketId, SupportTicketActionName.priority, priority).subscribe();
    },
    [updateTicketAction],
  );

  const onSelectSupportStatus = useCallback(
    (ticketId: TicketId, statusId: SupportTicketStatusId) => {
      updateTicketAction(ticketId, SupportTicketActionName.supportStatusId, statusId).subscribe();
    },
    [updateTicketAction],
  );

  const controls = useMemo(() => {
    return localTickets.map(ticket =>
      formControl<UserId>({
        placeholder: 'Assignee',
        initialValue: ticket.assignedTo?.id,
      }),
    );
  }, [localTickets]);

  const updateTicketAssignee = useCallback(
    (ticketId: TicketId, userId: UserId) => {
      onAssignTo(ticketId, SupportTicketActionName.assignedTo, userId).subscribe();
    },
    [onAssignTo],
  );

  const usersItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id'), [users]);

  const onSort = useCallback(
    (column: TableColumn<CompactSupportTicketResponse>, sortDirection: 'asc' | 'desc') => {
      paginated.patchQuery({ sortBy: column.sortField as string, sortDirection }).subscribe();
    },
    [paginated],
  );

  // Column click handler (switch from manual to column sort)
  const handleColumnClick = useCallback(
    (sortField: string) => {
      if (sortField && isManualSort && onColumnClick) {
        onColumnClick(sortField, 'desc');
      }
    },
    [isManualSort, onColumnClick],
  );

  // Drag Start Handler
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as TicketId);
  }, []);

  // Drag End Handler
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = localTickets.findIndex(t => t.id === active.id);
      const newIndex = localTickets.findIndex(t => t.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Optimistic UI update
      const reorderedTickets = arrayMove(localTickets, oldIndex, newIndex);
      setLocalTickets(reorderedTickets);

      // Save to backend
      setIsSaving(true);
      const orderedIds = reorderedTickets.map(t => t.id);
      const currentPage = paginated.pageQuery$.value.page || 1;
      const perPage = paginated.pageQuery$.value.perPage || 20;

      if (dragSaveSubscriptionRef.current) {
        dragSaveSubscriptionRef.current.unsubscribe();
      }

      dragSaveSubscriptionRef.current = onSaveOrder(query.channelId, orderedIds, currentPage, perPage)
        .pipe(
          tap({
            next: () => {
              setIsSaving(false);
            },
            error: error => {
              setIsSaving(false);
              setLocalTickets(tickets);
            },
          }),
        )
        .subscribe();
    },
    [localTickets, onSaveOrder, query.channelId, paginated, tickets],
  );

  const handleSearch = useCallback(
    (search: string) => {
      paginated.patchQuery({ search, page: 1 } as Partial<TicketSearchParams>).subscribe();
    },
    [paginated],
  );

  useLazyEffect(() => handleSearch(searchText), [searchText, handleSearch], 300);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.length === localTickets.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(localTickets.map(t => t.id));
    }
  }, [selectedIds.length, localTickets]);

  const handleSelectRow = useCallback((ticketId: TicketId) => {
    setSelectedIds(prev => {
      if (prev.includes(ticketId)) {
        return prev.filter(id => id !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  }, []);

  const columns: GCColumn<CompactSupportTicketResponse>[] = useMemo(
    () => [
      {
        id: 'priority',
        width: '50px',
        name: ' ',
        sortField: 'priority',
        sortable: !isManualSort,
        cell: ticket => (
          <UncontrolledDropdown style={{ width: '100%' }}>
            <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center">
              <i style={{ color: TicketService.getPriorityColor(ticket.priority) }} className="fa fa-flag"></i>
            </DropdownToggle>

            <DropdownMenu container="body" className="dd-menu_outside">
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.normal)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.normal) }} className="fa fa-flag mr-2"></i>Normal
              </a>
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.major)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.major) }} className="fa fa-flag mr-2"></i>Major
              </a>
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.critical)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.critical) }} className="fa fa-flag mr-2"></i>Critical
              </a>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
      {
        id: 'statusColumn',
        name: 'Status',
        width: '200px',
        sortable: !isManualSort,
        sortField: 'status',
        cell: ticket => (
          <StatusCell ticket={ticket} statuses={supportTicketStatuses} onSelectStatus={onSelectSupportStatus} onAddStatus={onAddSupportStatus} />
        ),
      },
      {
        id: 'subject',
        name: 'Subject',
        minWidth: '300px',
        sortable: !isManualSort,
        sortField: 'subject',
        cell: ticket => <SubjectItem ticket={ticket} />,
      },
      {
        id: 'org',
        name: 'Organizations',
        maxWidth: '200px',
        sortable: !isManualSort,
        sortField: 'organization',
        cell: ticket => (
          <div className="py-2">
            {!R.isEmpty(ticket.orgName) && (
              <a className="text-primary" href={`/organizations/${ticket.orgId}`} target="_blank" rel="noopener noreferrer">
                {ticket.orgName}
              </a>
            )}
            <a
              className="text-primary"
              onClick={() => {
                ticket.orgId != '0'
                  ? openSelectOrgModal({
                      onSelect: (orgId: OrgId) => updateTicketAction(ticket.id, SupportTicketActionName.orgId, orgId),
                    })
                  : openTicketOrg({ ticketId: ticket.id, email: ticket.fromEmail, name: ticket.fromName });
              }}
            >
              {R.isEmpty(ticket.orgName) && <span>---</span>}
              <i className="bx bx-edit ml-2"></i>
            </a>
          </div>
        ),
      },

      {
        id: 'assignedTo',
        name: 'Assigned To',
        maxWidth: '205px',
        sortable: !isManualSort,
        sortField: 'assignedTo',
        cell: (ticket, index) => (
          <div className="w-100">
            {!!controls[index] && (
              <FormSelect withoutGroup items={usersItems} control={controls[index]} onChange={userId => updateTicketAssignee(ticket.id, userId)} />
            )}
          </div>
        ),
      },
      {
        id: 'lastActivity',
        name: 'Last Activity',
        maxWidth: '125px',
        sortable: !isManualSort,
        sortField: 'lastActivity',
        cell: ticket => (
          <div className="py-2">
            <p className="mb-2 l-h-1 ">{ticket.lastActivityTime ? toMoment(ticket.lastActivityTime).fromNow() : ''}</p>
            <div className="l-h-1 text-muted font-size-12">{Formatter.formatTime(ticket.lastActivityTime, 'MMM D')}</div>
          </div>
        ),
      },

      {
        id: 'createdAt',
        name: 'Created',
        maxWidth: '125px',
        sortable: !isManualSort,
        sortField: 'createdAt',
        cell: ticket => (
          <div className="l-h-1">
            {Formatter.formatTime(ticket.createdAt, toMoment(ticket.createdAt).get('year') == toMoment().get('year') ? 'MMM D' : 'MMM D, YYYY')}
          </div>
        ),
      },
    ],
    [
      controls,
      onPriorityUpdate,
      onSelectSupportStatus,
      supportTicketStatuses,
      onAddSupportStatus,
      updateTicketAction,
      updateTicketAssignee,
      usersItems,
      isManualSort,
    ],
  );

  const columnRenderers = useMemo(() => {
    return {
      priority: columns.find(c => c.id === 'priority')?.cell,
      statusColumn: columns.find(c => c.id === 'statusColumn')?.cell,
      subject: columns.find(c => c.id === 'subject')?.cell,
      org: columns.find(c => c.id === 'org')?.cell,
      assignedTo: columns.find(c => c.id === 'assignedTo')?.cell,
      lastActivity: columns.find(c => c.id === 'lastActivity')?.cell,
      createdAt: columns.find(c => c.id === 'createdAt')?.cell,
    };
  }, [columns]);

  const statusItems = useMemo(
    () => CommonService.selectItems(TicketService.allStatuses(true), i => TicketService.getStatusTitle(i, false, true)),
    [],
  );

  const onSubmit = useCallback(
    ({ action }) => {
      return onUpdateBulk(selectedIds, action).pipe(tap(() => setToggleClearSelectedRows(true)));
    },
    [onUpdateBulk, selectedIds],
  );

  useFormConfig(form, { onSubmit });

  const activeTicket = useMemo(() => localTickets.find(t => t.id === activeId), [activeId, localTickets]);

  if (isManualSort) {
    const allSelected = selectedIds.length === localTickets.length && localTickets.length > 0;
    const someSelected = selectedIds.length > 0 && selectedIds.length < localTickets.length;

    return (
      <div>
        {/* Search Bar + Bulk Actions */}
        <div className="ticket-draggable-subheader">
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', gap: '10px', alignItems: 'center' }}>
              {/* Bulk Actions (show when items selected) */}
              {selectedIds.length > 0 && (
                <>
                  <span style={{ fontSize: '13px', color: '#495057' }}>{selectedIds.length} selected</span>
                  <div style={{ width: 200 }}>
                    <FormSelect withoutGroup control={form.controls.action} items={statusItems} placeholder="Bulk action..." />
                  </div>
                  <Button size="sm" onClick={() => form.submit()}>
                    GO
                  </Button>
                  <button className="btn btn-sm btn-light" onClick={() => setSelectedIds([])} style={{ marginLeft: 8 }}>
                    Clear
                  </button>
                </>
              )}
            </div>

            {/* Search Input */}
            <Input
              type="text"
              placeholder="Search"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 200 }}
              bsSize="sm"
            />
          </div>
        </div>
        <div className="ticket-draggable-table-wrapper bordered striped">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            {/* Table Header */}
            <div className="ticket-draggable-thead">
              <div className="ticket-draggable-header-row">
                {/* Drag Handle Header */}
                <div className="ticket-draggable-header-cell ticket-draggable-header-cell-drag">
                  <i className="bx bx-grid-vertical"></i>
                </div>

                {/* Checkbox Header (Select All) */}
                <div
                  className="ticket-draggable-header-cell ticket-hcell-select"
                  style={{ width: '50px', minWidth: '50px', cursor: 'pointer' }}
                  onClick={handleSelectAll}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={input => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>

                {/* Priority Flag Header */}
                <div className="ticket-draggable-header-cell" style={{ width: '50px', minWidth: '50px' }}>
                  {' '}
                </div>

                {/* Status Header */}
                <div
                  className="ticket-draggable-header-cell sortable"
                  style={{ width: '200px', minWidth: '200px' }}
                  onClick={() => handleColumnClick('status')}
                >
                  Status
                  <i className="bx bx-sort-up sort-icon"></i>
                </div>

                {/* Subject Header */}
                <div
                  className="ticket-draggable-header-cell sortable"
                  style={{ minWidth: '300px', flex: 1 }}
                  onClick={() => handleColumnClick('subject')}
                >
                  Subject
                  <i className="bx bx-sort-up sort-icon"></i>
                </div>

                {/* Organizations Header */}
                <div
                  className="ticket-draggable-header-cell sortable"
                  style={{ width: '200px', maxWidth: '200px' }}
                  onClick={() => handleColumnClick('organization')}
                >
                  Organizations
                  <i className="bx bx-sort-up sort-icon"></i>
                </div>

                {/* Assigned To Header */}
                <div
                  className="ticket-draggable-header-cell sortable"
                  style={{ width: '205px', maxWidth: '205px' }}
                  onClick={() => handleColumnClick('assignedTo')}
                >
                  Assigned To
                  <i className="bx bx-sort-up sort-icon"></i>
                </div>

                {/* Last Activity Header */}
                <div
                  className="ticket-draggable-header-cell sortable"
                  style={{ width: '125px', maxWidth: '125px' }}
                  onClick={() => handleColumnClick('lastActivity')}
                >
                  Last Activity
                  <i className="bx bx-sort-up sort-icon"></i>
                </div>

                {/* Created Header */}
                <div
                  className="ticket-draggable-header-cell sortable"
                  style={{ width: '125px', maxWidth: '125px' }}
                  onClick={() => handleColumnClick('createdAt')}
                >
                  Created
                  <i className="bx bx-sort-up sort-icon"></i>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <SortableContext items={localTickets.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div className="ticket-draggable-tbody">
                {localTickets.map((ticket, index) => {
                  const isSelected = selectedIds.includes(ticket.id);

                  return (
                    <DraggableRow key={ticket.id} ticket={ticket}>
                      <div
                        className="ticket-draggable-cell ticket-cell-select"
                        style={{ width: '50px', minWidth: '50px', cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          handleSelectRow(ticket.id);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(ticket.id)}
                          onClick={e => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>

                      {/* Priority Flag Cell */}
                      <div className="ticket-draggable-cell ticket-cell-priority" style={{ width: '50px', minWidth: '50px' }}>
                        {columnRenderers.priority?.(ticket, index, columns[0], ticket.id)}
                      </div>

                      {/* Status Cell */}
                      <div className="ticket-draggable-cell" data-label="Status" style={{ width: '200px', minWidth: '200px' }}>
                        {columnRenderers.statusColumn?.(ticket, index, columns[1], ticket.id)}
                      </div>

                      {/* Subject Cell */}
                      <div className="ticket-draggable-cell ticket-cell-subject" style={{ minWidth: '300px', flex: 1 }}>
                        {columnRenderers.subject?.(ticket, index, columns[2], ticket.id)}
                      </div>

                      {/* Organization Cell */}
                      <div className="ticket-draggable-cell" data-label="Organizations" style={{ width: '200px', maxWidth: '200px' }}>
                        {columnRenderers.org?.(ticket, index, columns[3], ticket.id)}
                      </div>

                      {/* Assigned To Cell */}
                      <div className="ticket-draggable-cell" data-label="Assigned To" style={{ width: '205px', maxWidth: '205px' }}>
                        {columnRenderers.assignedTo?.(ticket, index, columns[4], ticket.id)}
                      </div>

                      {/* Last Activity Cell */}
                      <div className="ticket-draggable-cell" data-label="Last Activity" style={{ width: '125px', maxWidth: '125px' }}>
                        {columnRenderers.lastActivity?.(ticket, index, columns[5], ticket.id)}
                      </div>

                      {/* Created Cell */}
                      <div className="ticket-draggable-cell" data-label="Created" style={{ width: '125px', maxWidth: '125px' }}>
                        {columnRenderers.createdAt?.(ticket, index, columns[6], ticket.id)}
                      </div>
                    </DraggableRow>
                  );
                })}
              </div>
            </SortableContext>

            {/* Drag Overlay */}
            <DragOverlay>
              {activeTicket ? (
                <div className="ticket-drag-overlay">
                  <div className="ticket-drag-handle">
                    <div className="ticket-drag-handle-icon">
                      <i className="bx bx-grid-vertical"></i>
                    </div>
                  </div>

                  {/* Checkbox (in overlay - disabled) */}
                  <div className="ticket-draggable-cell" style={{ width: '50px', minWidth: '50px' }}>
                    <input type="checkbox" disabled checked={selectedIds.includes(activeTicket.id)} />
                  </div>

                  {/* Priority Flag */}
                  <div className="ticket-draggable-cell" style={{ width: '50px', minWidth: '50px' }}>
                    {columnRenderers.priority?.(activeTicket, 0, columns[0], activeTicket.id)}
                  </div>

                  {/* Status */}
                  <div className="ticket-draggable-cell" style={{ width: '200px', minWidth: '200px' }}>
                    {columnRenderers.statusColumn?.(activeTicket, 0, columns[1], activeTicket.id)}
                  </div>

                  {/* Subject */}
                  <div className="ticket-draggable-cell ticket-cell-subject" style={{ minWidth: '300px', flex: 1 }}>
                    {columnRenderers.subject?.(activeTicket, 0, columns[2], activeTicket.id)}
                  </div>

                  {/* Organization */}
                  <div className="ticket-draggable-cell" style={{ width: '200px', maxWidth: '200px' }}>
                    {columnRenderers.org?.(activeTicket, 0, columns[3], activeTicket.id)}
                  </div>

                  {/* Assigned To */}
                  <div className="ticket-draggable-cell" style={{ width: '205px', maxWidth: '205px' }}>
                    {columnRenderers.assignedTo?.(activeTicket, 0, columns[4], activeTicket.id)}
                  </div>

                  {/* Last Activity */}
                  <div className="ticket-draggable-cell" style={{ width: '125px', maxWidth: '125px' }}>
                    {columnRenderers.lastActivity?.(activeTicket, 0, columns[5], activeTicket.id)}
                  </div>

                  {/* Created */}
                  <div className="ticket-draggable-cell" style={{ width: '125px', maxWidth: '125px' }}>
                    {columnRenderers.createdAt?.(activeTicket, 0, columns[6], activeTicket.id)}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {/* Pagination */}
          {pageMeta && (
            <Pagination
              currentPage={pageMeta.current_page}
              totalRows={pageMeta.total}
              rowsPerPage={pageMeta.per_page}
              onChangePage={page => paginated.patchQuery({ page } as Partial<TicketSearchParams>).subscribe()}
              onChangeRowsPerPage={perPage => paginated.patchQuery({ perPage, page: 1 } as Partial<TicketSearchParams>).subscribe()}
            />
          )}

          {/* Loading indicator */}
          {isSaving && (
            <div className="ticket-draggable-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Saving...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <GCTable
        initialSearchString={initialSearchString}
        columns={columns}
        data={localTickets}
        paginated={paginated}
        selectableRows
        selectableRowsHighlight
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={toggledClearRows}
        sortServer
        onSort={onSort}
        defaultSortField="lastActivityTime"
        defaultSortAsc={false}
        paginationPerPage={20}
        paginationRowsPerPageOptions={[20, 40, 100]}
      >
        {selectedIds.length > 0 && (
          <div style={{ flex: 1, flexDirection: 'row', display: 'flex' }}>
            <div style={{ width: 200, marginRight: 10 }}>
              <FormSelect withoutGroup control={form.controls.action} items={statusItems} />
            </div>
            <Button onClick={() => form.submit()}>GO</Button>
          </div>
        )}
      </GCTable>
    </div>
  );
});
